import React, { useRef, useEffect, useState, useCallback } from "react";

/**
 * ScrollableTable — wraps a <table> in a horizontally scrollable container
 * with two visual cues for mobile users:
 *
 * 1. A "← Scroll for more →" text hint (only shown when the table overflows)
 * 2. A right-edge fade gradient (wider & more prominent than before)
 *
 * Both cues disappear once the user scrolls fully to the right.
 */
const ScrollableTable = ({ children }) => {
  const scrollRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  // Check if the table overflows its container
  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflows = el.scrollWidth > el.clientWidth + 2;
    setHasOverflow(overflows);
    if (!overflows) setScrolledToEnd(false);
  }, []);

  useEffect(() => {
    checkOverflow();
    // Re-check on window resize (e.g. device rotation)
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  // Also re-check whenever children change (e.g. new players added)
  useEffect(() => {
    checkOverflow();
  }, [children, checkOverflow]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    setScrolledToEnd(atEnd);
  };

  const showHint = hasOverflow && !scrolledToEnd;
  const showFade = hasOverflow && !scrolledToEnd;

  return (
    <div className="card" style={{ position: "relative" }}>
      {/* Scroll hint text */}
      {showHint && (
        <div
          style={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#999",
            padding: "0.35rem 0",
            letterSpacing: "0.02em",
          }}
        >
          ← Scroll for more →
        </div>
      )}

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        style={{ overflowX: "auto" }}
        onScroll={handleScroll}
      >
        {children}
      </div>

      {/* Right-edge fade gradient */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "48px",
          background:
            "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))",
          pointerEvents: "none",
          transition: "opacity 0.25s",
          opacity: showFade ? 1 : 0,
          borderRadius: "0 12px 12px 0",
        }}
      />
    </div>
  );
};

export default ScrollableTable;
